import { useEffect, useMemo, useState } from 'react';
import { ApiRequest } from '@/utils/axios';
import { cartStore } from '@/stores/cart.store';
import { modalStore } from '@/stores/modal.store';
import useUserStore from '@/stores/user.store';
import { notify } from '@/utils/notify';
import SuccessIcon from '@/components/Notifications/SuccessIcon';
import AlertIcon from '@/components/Notifications/AlertIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';
import { IBaseItem, IMarketItem } from '@/utils/types/Items';
import { compare } from '@/utils/itemInfo';
import Toolbar, { SortMode } from './Toolbar';
import SkinGrid from './SkinGrid';
import CartPanel from './CartPanel';
import MobileCartDrawer from './MobileCartDrawer';

const PAGE_SIZE = 32;

const MarketPage = () => {
    const { setSelectedById, selectedItemIds, clearCart } = cartStore((state) => state);
    const { fetchUser, user } = useUserStore((state) => state);
    const { close } = modalStore((state) => state);

    const [items, setItems] = useState<IBaseItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState<SortMode>('high');
    const [page, setPage] = useState(1);
    const [buying, setBuying] = useState(false);
    const [cartOpen, setCartOpen] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const {
                data: {
                    response: { items: raw },
                },
            }: { data: { response: { items: IMarketItem[] } } } =
                await ApiRequest.get('/market/list');
            setItems(
                raw.map((it) => ({
                    id: it.id,
                    name: it.item.name,
                    priceUsd: it.item.priceUsd,
                    appId: it.item.appId,
                    icon: it.item.icon,
                    colorHex: it.item.colorHex,
                    iconBackground: it.item.iconBackground,
                }))
            );
            clearCart();
        } catch {}
        setLoading(false);
    }

    useEffect(() => {
        load();
        return () => clearCart();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        const arr = items.filter((it) => !s || it.name.toLowerCase().includes(s));
        return arr.sort((a, b) => {
            if (sort === 'high' || sort === 'low') return compare(a, b, sort === 'high' ? 'highToLow' : 'lowToHigh');
            return sort === 'az' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
        });
    }, [items, search, sort]);

    const selectedItems = useMemo(
        () => items.filter((it) => selectedItemIds.has(it.id)),
        [items, selectedItemIds]
    );
    const total = useMemo(
        () => selectedItems.reduce((s, it) => s + it.priceUsd, 0),
        [selectedItems]
    );

    const insufficient = total > (user.balanceUsd ?? 0);
    const canBuy = selectedItems.length > 0 && !insufficient && !buying;

    async function purchase() {
        if (!canBuy) return;
        setBuying(true);
        const itemIds = Array.from(selectedItemIds).map((id) => Number(id));
        try {
            const {
                data: { success },
            } = await ApiRequest.post('/market/buy/balance', { itemIds });
            if (success) {
                await fetchUser();
                await load();
                setPage(1);
                close();
                notify(
                    'success',
                    <SuccessIcon />,
                    <NotifyWrapper title="Success" text="You successfully bought new skins" />
                );
            } else {
                notify(
                    'error',
                    <AlertIcon />,
                    <NotifyWrapper title="Error" text="Purchase failed" />
                );
            }
        } finally {
            setBuying(false);
        }
    }

    const cartPanel = (
        <CartPanel
            items={selectedItems}
            onRemove={setSelectedById}
            total={total}
            balance={user.balanceUsd ?? 0}
            warning={insufficient ? 'Not enough balance for this purchase' : undefined}
            footer={
                <button
                    onClick={() => {
                        setCartOpen(false);
                        purchase();
                    }}
                    disabled={!canBuy}
                    className="group relative overflow-hidden w-full py-3.5 rounded-fl font-chat-nick text-[13px] font-extrabold tracking-[0.10em] uppercase inline-flex items-center justify-center gap-2.5 text-white transition-transform enabled:hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{
                        background: 'linear-gradient(135deg, #46e08a, #2bb89b)',
                        boxShadow:
                            'inset 0 1px 0 rgba(255,255,255,0.35), 0 0 22px -4px rgba(70,224,138,0.7), 0 8px 20px -6px rgba(43,184,155,0.5)',
                    }}
                >
                    <span
                        aria-hidden
                        className="pointer-events-none absolute inset-0 -translate-x-[120%] group-hover:translate-x-[120%] transition-transform duration-[700ms] ease-out"
                        style={{
                            background:
                                'linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.5), transparent 70%)',
                        }}
                    />
                    <svg
                        viewBox="0 0 24 24"
                        className="relative w-4 h-4 stroke-current fill-none [stroke-width:2.4] [stroke-linecap:round] [stroke-linejoin:round]"
                    >
                        <circle cx="9" cy="20" r="1.5" />
                        <circle cx="18" cy="20" r="1.5" />
                        <path d="M3 4h2l2.6 12.5a2 2 0 0 0 2 1.5h7.5a2 2 0 0 0 2-1.6L21 7H6" />
                    </svg>
                    <span className="relative">{buying ? 'Buying…' : 'Buy now'}</span>
                </button>
            }
        />
    );

    // Height budget: 100vh − sticky TopNav (68px) − grid pt-3/pb-3 (24px) so the
    // whole marketplace fits one screen without page scroll on xl+.
    return (
        <div className="flex flex-col gap-3 min-w-0 animate-fluent-fadeup xl:h-[calc(100vh-92px)]">
            {/* Page header */}
            <section className="surface flex flex-wrap items-center justify-between gap-3 px-4 py-3 shrink-0">
                <div className="flex items-center gap-2.5 min-w-0">
                    <span
                        className="w-1 h-[34px] rounded-sm shrink-0"
                        style={{
                            background: 'linear-gradient(#8b6dff,#3ad6ff)',
                            boxShadow: '0 0 12px #8b6dff',
                        }}
                    />
                    <div className="flex flex-col leading-tight min-w-0">
                        <h1 className="font-chat-nick text-[15px] font-extrabold tracking-[0.10em] uppercase text-text_main">
                            Marketplace
                        </h1>
                        <span className="text-[11.5px] text-text_3 font-medium truncate">
                            Buy skins straight to your balance — pick, stack the cart, check out.
                        </span>
                    </div>
                </div>
                <span className="font-chat-nick inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-layer_2 border border-stroke text-[12px] font-bold tracking-[0.06em] text-text_2 max-md:hidden">
                    <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: '#46e08a', boxShadow: '0 0 8px #46e08a' }}
                    />
                    {filtered.length} skins live
                </span>
            </section>

            <div className="grid grid-cols-[minmax(0,1fr)_320px] gap-3 min-w-0 flex-1 min-h-0 max-xl:grid-cols-1 max-2xl:grid-cols-[minmax(0,1fr)_280px]">
                <section className="flex flex-col gap-3 min-w-0 min-h-0">
                    <Toolbar
                        search={search}
                        onSearch={setSearch}
                        sort={sort}
                        onSort={setSort}
                        itemsCount={filtered.length}
                        onRefresh={load}
                        cartCount={selectedItems.length}
                        onCartOpen={() => setCartOpen(true)}
                    />
                    <SkinGrid
                        items={filtered}
                        selectedItemIds={selectedItemIds}
                        onToggle={setSelectedById}
                        page={page}
                        setPage={setPage}
                        pageSize={PAGE_SIZE}
                        loading={loading}
                        emptyMessage={
                            search ? 'No items match your search.' : 'Marketplace is empty.'
                        }
                    />
                </section>

                <div className="max-md:hidden contents">{cartPanel}</div>

                <MobileCartDrawer open={cartOpen} onClose={() => setCartOpen(false)}>
                    {cartPanel}
                </MobileCartDrawer>
            </div>
        </div>
    );
};

export default MarketPage;
